import MDEditor from "@uiw/react-md-editor"
import { useCallback, useEffect, useRef } from "react"
import { highlightPromptTemplate } from "@pages/main/scripts/prompts/sub_components/promptHighlight"
import { rehypePromptTokens } from "@pages/main/scripts/prompts/sub_components/rehypePromptTokens"

interface HighlightedMDEditorProps {
  value: string
  onChange: (value: string) => void
  height?: string | number
}

/**
 * MDEditor wrapper that repaints template tokens on the editor's own "pre" mirror layer. Spans
 * carry colour and background only — a border or font-weight would shift the overlay off the
 * textarea underneath it.
 */
export default function HighlightedMDEditor({
  value,
  onChange,
  height = "100%"
}: HighlightedMDEditorProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const highlightVariables = useCallback(() => {
    if (!containerRef.current) return

    const preElement = containerRef.current.querySelector(
      ".w-md-editor-text-pre > code"
    )
    if (!preElement) return

    preElement.innerHTML = highlightPromptTemplate(value || "")
  }, [value])

  useEffect(() => {
    const timeoutId = setTimeout(highlightVariables, 10)
    return () => clearTimeout(timeoutId)
  }, [highlightVariables, value])

  useEffect(() => {
    if (!containerRef.current) return

    const textArea = containerRef.current.querySelector(
      ".w-md-editor-text-input"
    )
    if (textArea) {
      textArea.addEventListener("scroll", highlightVariables)
      return () => textArea.removeEventListener("scroll", highlightVariables)
    }
  }, [highlightVariables])

  return (
    <div
      ref={containerRef}
      className="highlighted-md-editor-container"
      style={{ height }}
    >
      <MDEditor
        height={height}
        value={value}
        onChange={(v) => onChange(v ?? "")}
        previewOptions={{ rehypePlugins: [rehypePromptTokens] }}
      />

      <style>{`
        .highlighted-md-editor-container {
          position: relative;
        }

        .highlighted-md-editor-container .w-md-editor {
          height: 100% !important;
        }

        .pv-variable {
          color: #1b5e20;
          font-weight: inherit;
          background-color: rgba(46, 125, 50, 0.15);
        }

        .pv-if-tag {
          color: #e65100;
          font-weight: inherit;
          background-color: rgba(230, 81, 0, 0.22);
        }

        .pv-if-body {
          background-color: rgba(230, 81, 0, 0.07);
        }

        .pv-block-tag {
          color: #6a1b9a;
          font-weight: inherit;
          background-color: rgba(106, 27, 154, 0.2);
        }

        .pv-block-body {
          background-color: rgba(106, 27, 154, 0.07);
        }

        [data-color-mode="dark"] .pv-variable {
          color: #a5d6a7;
          background-color: rgba(76, 175, 80, 0.25);
        }

        [data-color-mode="dark"] .pv-if-tag {
          color: #ffb74d;
          background-color: rgba(255, 152, 0, 0.28);
        }

        [data-color-mode="dark"] .pv-if-body {
          background-color: rgba(255, 152, 0, 0.1);
        }

        [data-color-mode="dark"] .pv-block-tag {
          color: #ce93d8;
          background-color: rgba(186, 104, 200, 0.28);
        }

        [data-color-mode="dark"] .pv-block-body {
          background-color: rgba(186, 104, 200, 0.1);
        }
      `}</style>
    </div>
  )
}
